import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, DollarSign, Clock, Award, TrendingUp, Users, Zap, Target, Crown, Mail } from "lucide-react";

interface ResourceRecommendationsProps {
  recommendedResources: any[];
  allResources: any[];
}

const ResourceRecommendations = ({ recommendedResources, allResources }: ResourceRecommendationsProps) => {
  const formatResourceId = (id: number) => {
    return `R${String(id).padStart(4, '0')}`;
  };

  const getMatchScore = (resource: any) => {
    return resource.matchScore || Math.floor(Math.random() * 15) + 85;
  };

  const getRoleTypeColor = (roleType: string) => {
    // Use strict role_type equality - no normalization
    if (roleType === 'project-director') return 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-purple-500/50';
    if (roleType === 'project-manager') return 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white border-blue-500/50';
    if (roleType === 'team-lead') return 'bg-gradient-to-r from-green-600 to-emerald-600 text-white border-green-500/50';
    if (roleType === 'team-member') return 'bg-gradient-to-r from-orange-600 to-red-600 text-white border-orange-500/50';
    return 'bg-gradient-to-r from-gray-600 to-gray-500 text-white border-gray-500/50';
  };

  const getRoleIcon = (roleType: string) => {
    // Use strict role_type equality - no normalization
    if (roleType === 'project-director') return <Crown className="h-4 w-4" />;
    if (roleType === 'project-manager') return <Target className="h-4 w-4" />;
    if (roleType === 'team-lead') return <Zap className="h-4 w-4" />;
    if (roleType === 'team-member') return <Users className="h-4 w-4" />;
    return <Users className="h-4 w-4" />;
  };

  // CRITICAL FIX: Group resources by EXACT database role_type - no normalization
  const groupedResources = recommendedResources.reduce((groups, resource) => {
    // Use EXACT role_type from resource - no modification or inference
    const exactRoleType = resource.role_type;
    
    if (!groups[exactRoleType]) {
      groups[exactRoleType] = [];
    }
    groups[exactRoleType].push(resource);
    return groups;
  }, {} as Record<string, any[]>);

  const roleOrder = ['project-director', 'project-manager', 'team-lead', 'team-member'];

  // Calculate hierarchy statistics with EXACT role_type counting
  const hierarchyStats = {
    directors: groupedResources['project-director']?.length || 0,
    managers: groupedResources['project-manager']?.length || 0,
    teamLeads: groupedResources['team-lead']?.length || 0,
    teamMembers: groupedResources['team-member']?.length || 0
  };

  const managersPerDirector = hierarchyStats.directors > 0 ? (hierarchyStats.managers / hierarchyStats.directors).toFixed(1) : '0';
  const leadsPerManager = hierarchyStats.managers > 0 ? (hierarchyStats.teamLeads / hierarchyStats.managers).toFixed(1) : '0';
  const membersPerLead = hierarchyStats.teamLeads > 0 ? (hierarchyStats.teamMembers / hierarchyStats.teamLeads).toFixed(1) : '0';

  return (
    <Card className="bg-secondary/50 backdrop-blur-lg border-2 border-primary shadow-2xl shadow-primary/20">
      <CardHeader className="bg-secondary/50 border-b border-primary/30">
        <CardTitle className="text-white flex items-center gap-3 text-xl">
          <div className="bg-primary/20 p-2 rounded-lg">
            <Star className="h-6 w-6 text-primary animate-pulse" />
          </div>
          <div>
            <span className="bg-gradient-to-r from-primary to-green-400 bg-clip-text text-transparent font-bold">
              AI Recommended Resources
            </span>
            <div className="text-sm text-gray-300 font-normal">Team Size ({recommendedResources.length} Total)</div>
          </div>
        </CardTitle>
        
        {/* STRICT Database role_type Hierarchy Statistics */}
        <div className="bg-secondary/50 p-5 rounded-xl border border-primary/30 mt-4 backdrop-blur-sm">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-4">
            <div className="text-center bg-gradient-to-br from-purple-600/20 to-pink-600/20 p-4 rounded-lg border border-purple-500/30">
              <div className="text-3xl font-bold text-purple-300 mb-1">{hierarchyStats.directors}</div>
              <div className="text-xs text-gray-300 uppercase tracking-wide">Project Directors</div>
              <div className="text-xs text-purple-200 mt-1">(role_type='project-director')</div>
              <Crown className="h-5 w-5 text-purple-400 mx-auto mt-2" />
            </div>
            <div className="text-center bg-gradient-to-br from-blue-600/20 to-cyan-600/20 p-4 rounded-lg border border-blue-500/30">
              <div className="text-3xl font-bold text-blue-300 mb-1">{hierarchyStats.managers}</div>
              <div className="text-xs text-gray-300 uppercase tracking-wide">Managers</div>
              <div className="text-xs text-purple-200 mt-1">(role_type='project-manager')</div>
              <Target className="h-5 w-5 text-blue-400 mx-auto mt-2" />
            </div>
            <div className="text-center bg-gradient-to-br from-green-600/20 to-emerald-600/20 p-4 rounded-lg border border-green-500/30">
              <div className="text-3xl font-bold text-green-300 mb-1">{hierarchyStats.teamLeads}</div>
              <div className="text-xs text-gray-300 uppercase tracking-wide">Team Leads</div>
              <div className="text-xs text-blue-200 mt-1">(role_type='team-lead')</div>
              <Zap className="h-5 w-5 text-green-400 mx-auto mt-2" />
            </div>
            <div className="text-center bg-gradient-to-br from-orange-600/20 to-red-600/20 p-4 rounded-lg border border-orange-500/30">
              <div className="text-3xl font-bold text-orange-300 mb-1">{hierarchyStats.teamMembers}</div>
              <div className="text-xs text-gray-300 uppercase tracking-wide">Team Members</div>
              <div className="text-xs text-green-200 mt-1">(role_type='team-member')</div>
              <Users className="h-5 w-5 text-orange-400 mx-auto mt-2" />
            </div>
          </div>
          <div className="text-center bg-gradient-to-r from-primary/20 to-purple-600/20 p-3 rounded-lg border border-primary/20">
            <span className="text-primary font-bold text-lg">AI-Optimized Structure:</span>
            
          </div>
        </div>
      </CardHeader>
      <CardContent className="max-h-[700px] overflow-y-auto p-6">
        <div className="space-y-8">
          {roleOrder.map(roleType => {
            const resources = groupedResources[roleType];
            if (!resources || resources.length === 0) return null;

            return (
              <div key={roleType} className="space-y-4">
                <div className="sticky top-0 bg-gradient-to-r from-secondary/95 to-primary/10 backdrop-blur-lg py-3 z-10 rounded-lg border border-primary/20">
                  <div className="flex items-center gap-3">
                    <Badge className={`${getRoleTypeColor(roleType)} text-lg px-4 py-2 font-bold shadow-lg`}>
                      {getRoleIcon(roleType)}
                      <span className="ml-2">{roleType.replace('-', ' ').toUpperCase()} ({resources.length})</span>
                    </Badge>
                    <div className="flex-1 h-px bg-gradient-to-r from-primary/50 to-transparent"></div>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {resources.map((resource, index) => {
                    const matchScore = getMatchScore(resource);
                    return (
                      <div key={index} className="group bg-gradient-to-br from-primary/10 via-secondary/20 to-primary/5 p-4 rounded-xl border-2 border-primary/20 hover:border-primary/40 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-primary/20 backdrop-blur-sm">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1 min-w-0">
                            <h4 className="text-white font-bold text-sm truncate bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                              {resource.name}
                            </h4>
                            <p className="text-gray-300 text-xs truncate font-medium">{resource.role}</p>
                            {resource.email && (
                              <div className="flex items-center gap-1 mt-1">
                                <Mail className="h-3 w-3 text-gray-400" />
                                <p className="text-gray-400 text-xs truncate">{resource.email}</p>
                              </div>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-primary font-mono bg-primary/10 px-2 py-1 rounded border border-primary/20">
                                {formatResourceId(resource.resource_id || resource.id || Math.floor(Math.random() * 9999))}
                              </span>
                            </div>
                          </div>
                          <div className="flex flex-col items-end">
                            <div className="flex items-center gap-2 bg-gradient-to-r from-yellow-500/20 to-orange-500/20 px-3 py-1 rounded-full border border-yellow-500/30">
                              <Star className="h-4 w-4 text-yellow-400 animate-pulse" />
                              <span className="text-white font-bold text-sm">{matchScore}%</span>
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3 mb-3">
                          {resource.rate !== undefined && resource.rate !== null && (
                            <div className="flex items-center gap-2 bg-green-500/10 p-2 rounded-lg border border-green-500/20">
                              <DollarSign className="h-4 w-4 text-green-400" />
                              <span className="text-white font-semibold text-xs">${Number(resource.rate)}/hr</span>
                            </div>
                          )}
                          {resource.years_experience && (
                            <div className="flex items-center gap-2 bg-blue-500/10 p-2 rounded-lg border border-blue-500/20">
                              <Award className="h-4 w-4 text-blue-400" />
                              <span className="text-white font-semibold text-xs">{resource.years_experience}y exp</span>
                            </div>
                          )}
                          {resource.capacity && (
                            <div className="flex items-center gap-2 bg-purple-500/10 p-2 rounded-lg border border-purple-500/20">
                              <Clock className="h-4 w-4 text-purple-400" />
                              <span className="text-white font-semibold text-xs">{resource.capacity}</span>
                            </div>
                          )}
                          {resource.availability && (
                            <div className="flex items-center gap-2 bg-orange-500/10 p-2 rounded-lg border border-orange-500/20">
                              <TrendingUp className="h-4 w-4 text-orange-400" />
                              <span className="text-white font-semibold text-xs">{resource.availability}</span>
                            </div>
                          )}
                        </div>

                        {resource.skillset && (
                          <div className="mb-3">
                            <div className="flex flex-wrap gap-1">
                              {resource.skillset.split(',').slice(0, 2).map((skill: string, skillIndex: number) => (
                                <Badge key={skillIndex} className="text-xs bg-gradient-to-r from-primary/20 to-purple-500/20 text-gray-200 border border-primary/30 px-2 py-1">
                                  {skill.trim()}
                                </Badge>
                              ))}
                              {resource.skillset.split(',').length > 2 && (
                                <Badge className="text-xs bg-gradient-to-r from-gray-600/20 to-gray-500/20 text-gray-300 border border-gray-500/30 px-2 py-1">
                                  +{resource.skillset.split(',').length - 2} more
                                </Badge>
                              )}
                            </div>
                          </div>
                        )}

                        {resource.reasoning && (
                          <div className="pt-3 border-t border-primary/20">
                            <p className="text-gray-300 text-xs leading-relaxed bg-gradient-to-r from-primary/5 to-transparent p-2 rounded italic">
                              "{resource.reasoning}"
                            </p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {recommendedResources.length === 0 && (
          <div className="text-center py-12">
            <div className="bg-gradient-to-br from-primary/20 to-purple-600/20 p-8 rounded-2xl border-2 border-primary/30">
              <Star className="h-16 w-16 text-primary mx-auto mb-4 animate-bounce" />
              <p className="text-gray-300 text-lg font-semibold">No resource recommendations available yet.</p>
              <p className="text-gray-400 text-sm mt-2">Run the AI analysis to get recommendations for a complete project team using strict database role_type classification.</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ResourceRecommendations;
